import React, { useEffect, useState } from "react";
import { Topbar } from "@/components/layout/Topbar";
import { TrafficLight } from "@/components/rikkus/TrafficLight";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    ReferenceLine,
} from "recharts";
import { api } from "@/lib/api";
import { useSelectedDate } from "@/context/DateContext";
import { fmtET, fmtETShort } from "@/lib/time";
import { Activity, Clock, Waves, AlertTriangle, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

function MetricTile({ label, value, hint, testid, tone = "default" }) {
    const toneClass = {
        default: "",
        ok: "text-primary",
        warn: "text-warn",
        fail: "text-fail",
    }[tone];
    return (
        <div className="border border-border rounded-md p-3 bg-card" data-testid={testid}>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={cn("mt-1 font-mono tabular-nums text-2xl font-semibold", toneClass)}>
                {value ?? "—"}
            </div>
            {hint && <div className="mt-0.5 text-[10px] text-muted-foreground font-mono">{hint}</div>}
        </div>
    );
}

export default function AuditPage() {
    const { date } = useSelectedDate();
    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        Promise.all([
            api.get(`/audit/unified?date=${date}`).then((r) => r.data).catch(() => null),
            api.get(`/audit/history?days=14`).then((r) => r.data || []).catch(() => []),
        ])
            .then(([u, h]) => {
                if (!alive) return;
                setData(u);
                setHistory(h);
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [date]);

    const ca = data?.collection_audit;
    const pol = data?.poller;
    const polB = data?.poller_bet365;
    const status = ca?.status || (pol?.status ?? "UNKNOWN");
    const subtitle = data
        ? `Recolección + poller — ${data.audit_date} • ${data.track_id}`
        : `Sin datos para ${date}`;

    // Chart data
    const heartbeatChart = (data?.heartbeat_series || []).map((h) => ({
        t: new Date(h.beat_at).getTime(),
        drift: h.clock_drift_seconds != null ? Number(h.clock_drift_seconds) : null,
        races: h.races_being_polled ?? 0,
    }));
    const hourlyChart = (data?.snapshots_hourly || []).map((h) => ({
        hour: h.hour_et.replace(" ET", "").slice(-5),
        NYRA: h.nyra,
        BET365: h.bet365,
        OTHER: h.other,
    }));

    const uptimeTone = pol?.uptime_percent >= 99 ? "ok" : pol?.uptime_percent >= 90 ? "warn" : "fail";
    const driftTone =
        pol?.max_abs_clock_drift_seconds == null
            ? "default"
            : pol.max_abs_clock_drift_seconds > 2.0
            ? "fail"
            : pol.max_abs_clock_drift_seconds > 0.5
            ? "warn"
            : "ok";
    const gapsTone = !pol?.gaps_count ? "ok" : pol.gaps_count > 5 ? "fail" : "warn";

    return (
        <>
            <Topbar title="Auditoría" subtitle="Salud unificada: recolección + poller + snapshots" />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                {loading ? (
                    <div className="space-y-4">
                        <Skeleton className="h-24 w-full" />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[...Array(8)].map((_, i) => (
                                <Skeleton key={i} className="h-20" />
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* -------- 1. SEMÁFORO PRINCIPAL (colección) -------- */}
                        <div data-testid="audit-traffic-light-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <TrafficLight status={status} subtitle={subtitle} />
                            <TrafficLight
                                status={pol?.status || "UNKNOWN"}
                                subtitle={
                                    pol?.status_reason
                                        ? `Poller: ${pol.status_reason}`
                                        : pol?.status === "OK"
                                        ? "Poller: sin incidencias registradas"
                                        : "Poller: sin datos"
                                }
                            />
                        </div>

                        {/* -------- 1b. ALERTA + TIRA HISTÓRICA (últimos 14 días) -------- */}
                        <Card data-testid="audit-history-card">
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                                    Historial 14 días — {ca?.track_id || data?.track_id || "—"}
                                </CardTitle>
                                {ca?.alert_sent && (
                                    <div
                                        className="flex items-center gap-2 text-[11px] font-mono"
                                        data-testid="audit-alert-badge"
                                    >
                                        <span className="text-muted-foreground">Alerta:</span>
                                        <span className={cn(
                                            "px-2 py-0.5 rounded-sm border",
                                            ca.alert_last_status === "FAILED"
                                                ? "border-fail/40 text-fail bg-fail/10"
                                                : "border-warn/40 text-warn bg-warn/10"
                                        )}>
                                            {ca.alert_last_status || "?"}  · enviada {ca.alert_count || 1}× · {ca.alert_last_sent_at ? fmtETShort(ca.alert_last_sent_at) : "?"}
                                        </span>
                                    </div>
                                )}
                            </CardHeader>
                            <CardContent>
                                {history.length === 0 ? (
                                    <div className="text-xs text-muted-foreground font-mono py-3">
                                        Sin auditorías previas registradas.
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5" data-testid="audit-history-strip">
                                        {[...history].reverse().map((h) => {
                                            const c =
                                                h.status === "OK" ? "bg-primary/70 hover:bg-primary"
                                                : h.status === "DEGRADED" ? "bg-warn/70 hover:bg-warn"
                                                : h.status === "FAILED" ? "bg-fail/70 hover:bg-fail"
                                                : "bg-muted/40 hover:bg-muted";
                                            return (
                                                <div
                                                    key={h.audit_date}
                                                    data-testid={`audit-history-day-${h.audit_date}`}
                                                    className={cn(
                                                        "w-9 h-9 rounded-sm border border-border/60 flex flex-col items-center justify-center transition",
                                                        c,
                                                    )}
                                                    title={
                                                        `${h.audit_date} · ${h.status}` +
                                                        `\nExpected: ${h.races_expected ?? "—"}` +
                                                        `\nResults: ${h.races_with_results ?? "—"}` +
                                                        `\nMissing: ${h.races_missing_count}` +
                                                        `\nMissing T-1: ${h.races_missing_t1_count}` +
                                                        `\nParse failures: ${h.parse_failures_count ?? 0}` +
                                                        `\nUptime: ${h.poller_uptime_pct ?? "—"}%` +
                                                        `\nDrift máx: ${h.max_clock_drift_seconds ?? "—"}s`
                                                    }
                                                >
                                                    <span className="text-[9px] font-mono text-background/90 leading-none">
                                                        {h.audit_date.slice(5)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="mt-2 flex items-center gap-4 text-[10px] font-mono text-muted-foreground">
                                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-primary/70 rounded-sm" /> OK</span>
                                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-warn/70 rounded-sm" /> DEGRADED</span>
                                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-fail/70 rounded-sm" /> FAILED</span>
                                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-muted/40 rounded-sm" /> sin datos</span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* -------- 2. COLLECTION_AUDIT (recolección del cron) -------- */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Waves className="h-4 w-4 text-muted-foreground" />
                                    Recolección — {ca?.track_id || "—"}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <MetricTile
                                        label="Carreras esperadas"
                                        value={ca?.races_expected}
                                        testid="audit-metric-races-expected"
                                    />
                                    <MetricTile
                                        label="Carreras capturadas"
                                        value={ca?.races_captured}
                                        testid="audit-metric-races-captured"
                                    />
                                    <MetricTile
                                        label="Con resultados"
                                        value={ca?.races_with_results}
                                        testid="audit-metric-with-results"
                                    />
                                    <MetricTile
                                        label="Finish completo"
                                        value={ca?.races_with_full_finish}
                                        testid="audit-metric-full-finish"
                                    />
                                    <MetricTile
                                        label="Snapshots totales"
                                        value={data?.total_snapshots_today ?? ca?.snapshots_total}
                                        testid="audit-metric-snap-total"
                                    />
                                    <MetricTile
                                        label="Snapshots NYRA"
                                        value={data?.total_snapshots_nyra_today ?? ca?.snapshots_from_nyra}
                                        testid="audit-metric-snap-nyra"
                                    />
                                    <MetricTile
                                        label="Snapshots BET365"
                                        value={data?.total_snapshots_bet365_today}
                                        testid="audit-metric-snap-bet365"
                                    />
                                    <MetricTile
                                        label="Parse failures"
                                        value={data?.parse_failures_today ?? ca?.parse_failures_count ?? 0}
                                        testid="audit-metric-parse-failures"
                                        tone={data?.parse_failures_today > 0 ? "warn" : "default"}
                                    />
                                    <MetricTile
                                        label="Cobertura T-5 NYRA"
                                        value={`${ca?.races_with_t5_snapshot ?? 0} / ${ca?.races_expected ?? 0}`}
                                        hint={
                                            ca?.races_expected
                                                ? `${Math.round(100 * (ca.races_with_t5_snapshot || 0) / ca.races_expected)}%`
                                                : "—"
                                        }
                                        testid="audit-metric-t5-nyra"
                                        tone={
                                            !ca?.races_expected ? "default" :
                                            (ca?.races_with_t5_snapshot ?? 0) === ca.races_expected ? "ok" :
                                            (ca?.races_with_t5_snapshot ?? 0) === 0 ? "fail" : "warn"
                                        }
                                    />
                                    <MetricTile
                                        label="Cobertura T-1 NYRA"
                                        value={`${ca?.races_with_t1_snapshot ?? 0} / ${ca?.races_expected ?? 0}`}
                                        hint={
                                            ca?.races_missing_t1?.length
                                                ? `faltan: R${ca.races_missing_t1.join(",R")}`
                                                : "todas OK"
                                        }
                                        testid="audit-metric-t1-nyra"
                                        tone={
                                            !ca?.races_expected ? "default" :
                                            (ca?.races_with_t1_snapshot ?? 0) === ca.races_expected ? "ok" :
                                            (ca?.races_with_t1_snapshot ?? 0) === 0 ? "fail" : "warn"
                                        }
                                    />
                                    <MetricTile
                                        label="Carreras PARTIAL"
                                        value={ca?.races_partial ?? 0}
                                        hint="K≥5, entrada manual"
                                        testid="audit-metric-partial"
                                    />
                                    <MetricTile
                                        label="Cobertura T-1 BET365"
                                        value={`${ca?.races_with_t1_snapshot_bet365 ?? 0} / ${ca?.races_expected ?? 0}`}
                                        hint="fixed odds (informativo)"
                                        testid="audit-metric-t1-bet365"
                                    />
                                </div>
                                {ca?.notes && (
                                    <details className="mt-3" data-testid="audit-notes-details">
                                        <summary className="text-xs cursor-pointer text-muted-foreground hover:text-foreground">
                                            Notas del cron ({(ca.notes || "").split("\n").length} líneas)
                                        </summary>
                                        <pre className="mt-2 whitespace-pre-wrap text-[10px] font-mono text-muted-foreground max-h-40 overflow-auto">
                                            {ca.notes}
                                        </pre>
                                    </details>
                                )}
                            </CardContent>
                        </Card>

                        {/* -------- 2.5 OTB coverage (M2 add-on, migración 007) -------- */}
                        {data?.otb && (
                            <Card data-testid="audit-otb-card">
                                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                                        Segunda fuente — OTB (Top-4 verificado)
                                    </CardTitle>
                                    <div className="flex items-center gap-2">
                                        {data.otb.podium_mismatches > 0 && (
                                            <StatusBadge kind="miss" data-testid="audit-otb-mismatch-badge">
                                                {data.otb.podium_mismatches} MISMATCH
                                            </StatusBadge>
                                        )}
                                        {data.otb.podium_mismatches === 0 && data.otb.races_verified > 0 && (
                                            <StatusBadge kind="verified" data-testid="audit-otb-ok-badge">OK</StatusBadge>
                                        )}
                                        {data.otb.races_verified === 0 && (
                                            <StatusBadge kind="pending" data-testid="audit-otb-none-badge">SIN DATOS</StatusBadge>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <MetricTile
                                            label="Cobertura Top-4"
                                            value={
                                                data.otb.top4_verified_pct != null
                                                    ? `${data.otb.top4_verified_pct}%`
                                                    : "—"
                                            }
                                            hint="posiciones 1-4 confirmadas por OTB"
                                            testid="audit-otb-top4-pct"
                                            tone={
                                                data.otb.top4_verified_pct == null ? "default"
                                                : data.otb.top4_verified_pct >= 90 ? "ok"
                                                : data.otb.top4_verified_pct >= 50 ? "warn" : "fail"
                                            }
                                        />
                                        <MetricTile
                                            label="Carreras con OTB"
                                            value={data.otb.races_verified}
                                            hint="otb_verified_at NOT NULL"
                                            testid="audit-otb-races-verified"
                                        />
                                        <MetricTile
                                            label="Podio MATCH"
                                            value={data.otb.cross_check_match}
                                            hint="NYRA ≡ OTB en 1-2-3"
                                            testid="audit-otb-xc-match"
                                            tone="ok"
                                        />
                                        <MetricTile
                                            label="Podio MISMATCH"
                                            value={data.otb.cross_check_mismatch}
                                            hint="NYRA ≠ OTB → parse_failures"
                                            testid="audit-otb-xc-mismatch"
                                            tone={data.otb.cross_check_mismatch > 0 ? "fail" : "default"}
                                        />
                                        <MetricTile
                                            label="Podio incompleto"
                                            value={data.otb.cross_check_incomplete}
                                            hint="alguna fuente <3"
                                            testid="audit-otb-xc-incomplete"
                                        />
                                        <MetricTile
                                            label="PARTIAL K≥5"
                                            value={data.otb.races_partial_k5_plus}
                                            hint="pendientes entrada manual"
                                            testid="audit-otb-partial-k5"
                                            tone={data.otb.races_partial_k5_plus > 0 ? "warn" : "default"}
                                        />
                                        <MetricTile
                                            label="Último fetch OTB"
                                            value={
                                                data.otb.endpoint_last_ok_at
                                                    ? fmtETShort(data.otb.endpoint_last_ok_at)
                                                    : "—"
                                            }
                                            hint={
                                                data.otb.endpoint_last_status != null
                                                    ? `HTTP ${data.otb.endpoint_last_status}`
                                                    : "sin llamadas registradas"
                                            }
                                            testid="audit-otb-last-fetch"
                                            tone={
                                                data.otb.endpoint_last_status === 200 ? "ok"
                                                : data.otb.endpoint_last_status != null ? "warn"
                                                : "default"
                                            }
                                        />
                                        <MetricTile
                                            label="Endpoint OTB"
                                            value="execute-api"
                                            hint="⚠ hostname rotable"
                                            testid="audit-otb-endpoint-fragility"
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* -------- 3. POLLER HEALTH (Módulo 4) -------- */}
                        <Card>
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Activity className="h-4 w-4 text-muted-foreground" />
                                    Poller — tote alta frecuencia
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                    {pol?.status === "OK" && <StatusBadge kind="verified">OK</StatusBadge>}
                                    {pol?.status === "DEGRADED" && <StatusBadge kind="partial">DEGRADED</StatusBadge>}
                                    {pol?.status === "FAILED" && <StatusBadge kind="miss">FAILED</StatusBadge>}
                                    {pol?.status === "UNKNOWN" && <StatusBadge kind="pending">SIN DATOS</StatusBadge>}
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                                    <MetricTile
                                        label="Uptime del día"
                                        value={pol?.uptime_percent != null ? `${pol.uptime_percent.toFixed(2)}%` : "—"}
                                        hint={`${pol?.heartbeats_count ?? 0} / ${pol?.heartbeats_expected ?? 0} heartbeats`}
                                        testid="audit-poller-uptime"
                                        tone={uptimeTone}
                                    />
                                    <MetricTile
                                        label="Drift máx (abs)"
                                        value={
                                            pol?.max_abs_clock_drift_seconds != null
                                                ? `${pol.max_abs_clock_drift_seconds.toFixed(3)}s`
                                                : "—"
                                        }
                                        hint={`umbral 2.00s • avg ${
                                            pol?.avg_abs_clock_drift_seconds?.toFixed(3) ?? "—"
                                        }s`}
                                        testid="audit-poller-max-drift"
                                        tone={driftTone}
                                    />
                                    <MetricTile
                                        label="Gaps > 120s"
                                        value={pol?.gaps_count ?? 0}
                                        hint={
                                            pol?.longest_gap_seconds
                                                ? `mayor: ${pol.longest_gap_seconds.toFixed(0)}s`
                                                : "sin cortes"
                                        }
                                        testid="audit-poller-gaps"
                                        tone={gapsTone}
                                    />
                                    <MetricTile
                                        label="Último heartbeat"
                                        value={
                                            pol?.last_beat_age_seconds != null
                                                ? `${Math.round(pol.last_beat_age_seconds)}s`
                                                : "—"
                                        }
                                        hint={pol?.last_beat_at ? fmtETShort(pol.last_beat_at) : "—"}
                                        testid="audit-poller-last-beat"
                                        tone={
                                            pol?.last_beat_age_seconds == null
                                                ? "default"
                                                : pol.last_beat_age_seconds > 180
                                                ? "fail"
                                                : pol.last_beat_age_seconds > 90
                                                ? "warn"
                                                : "ok"
                                        }
                                    />
                                </div>

                                {/* Timeline heartbeats */}
                                {heartbeatChart.length > 0 ? (
                                    <div className="border border-border rounded-md p-3 bg-card">
                                        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                            Timeline heartbeats — drift (seg)
                                        </div>
                                        <div className="h-40 w-full" data-testid="audit-heartbeat-timeline">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={heartbeatChart} margin={{ top: 5, right: 15, bottom: 5, left: 0 }}>
                                                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                                                    <XAxis
                                                        dataKey="t"
                                                        tickFormatter={(t) => fmtETShort(new Date(t).toISOString())}
                                                        tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "'Source Code Pro', monospace" }}
                                                        stroke="hsl(var(--border))"
                                                    />
                                                    <YAxis
                                                        tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "'Source Code Pro', monospace" }}
                                                        stroke="hsl(var(--border))"
                                                        domain={["auto", "auto"]}
                                                    />
                                                    <Tooltip
                                                        contentStyle={{
                                                            background: "hsl(var(--card))",
                                                            border: "1px solid hsl(var(--border))",
                                                            borderRadius: 6,
                                                            color: "hsl(var(--card-foreground))",
                                                            fontFamily: "'Source Code Pro', monospace",
                                                            fontSize: 11,
                                                        }}
                                                        labelFormatter={(t) => fmtETShort(new Date(t).toISOString())}
                                                        formatter={(v) => (v != null ? Number(v).toFixed(3) + "s" : "—")}
                                                    />
                                                    <ReferenceLine y={2.0} stroke="hsl(var(--fail))" strokeDasharray="4 4" label={{ value: "límite 2s", fill: "hsl(var(--fail))", fontSize: 9 }} />
                                                    <ReferenceLine y={-2.0} stroke="hsl(var(--fail))" strokeDasharray="4 4" />
                                                    <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.4} />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="drift"
                                                        stroke="hsl(var(--primary))"
                                                        strokeWidth={1.6}
                                                        dot={{ r: 2 }}
                                                        connectNulls
                                                        isAnimationActive={false}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border border-border rounded-md p-6 bg-card text-center text-xs text-muted-foreground" data-testid="audit-heartbeat-empty">
                                        Sin heartbeats registrados para este día.
                                    </div>
                                )}

                                {/* Gaps detail (si los hay) */}
                                {pol?.gaps?.length > 0 && (
                                    <div className="mt-3 border border-warn/30 bg-warn-bg rounded-md p-3" data-testid="audit-poller-gaps-detail">
                                        <div className="flex items-center gap-2 text-warn text-xs font-semibold uppercase tracking-wide mb-2">
                                            <AlertTriangle className="h-3.5 w-3.5" />
                                            Gaps detectados
                                        </div>
                                        <div className="space-y-1">
                                            {pol.gaps.slice(0, 8).map((g, i) => (
                                                <div key={i} className="font-mono text-[11px] text-warn/90">
                                                    <span className="text-warn font-semibold">{g.gap_seconds.toFixed(0)}s</span>
                                                    <span className="text-muted-foreground mx-2">•</span>
                                                    {fmtETShort(g.start)} → {fmtETShort(g.end)}
                                                </div>
                                            ))}
                                            {pol.gaps.length > 8 && (
                                                <div className="text-[10px] text-muted-foreground">…y {pol.gaps.length - 8} más</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* -------- 3b. INGESTOR BET365 (Módulo 5) -------- */}
                        <Card data-testid="audit-bet365-card">
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Activity className="h-4 w-4 text-muted-foreground" />
                                    Ingestor bet365 — fixed odds (headless)
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                    {polB?.status === "OK" && <StatusBadge kind="verified">OK</StatusBadge>}
                                    {polB?.status === "DEGRADED" && <StatusBadge kind="partial">DEGRADED</StatusBadge>}
                                    {polB?.status === "FAILED" && <StatusBadge kind="miss">FAILED</StatusBadge>}
                                    {!polB && <StatusBadge kind="pending">SIN DATOS</StatusBadge>}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {!polB ? (
                                    <div
                                        className="p-4 text-center text-xs text-muted-foreground font-mono"
                                        data-testid="audit-bet365-empty"
                                    >
                                        Sin heartbeats del ingestor bet365 en este día.
                                        <br />
                                        (servicio no arrancado / VM sin conexión / assisted-login pendiente)
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <MetricTile
                                            label="Uptime del día"
                                            value={polB.uptime_percent != null ? `${polB.uptime_percent.toFixed(2)}%` : "—"}
                                            hint={`${polB.heartbeats_count ?? 0} / ${polB.heartbeats_expected ?? 0} heartbeats`}
                                            testid="audit-bet365-uptime"
                                            tone={polB.uptime_percent >= 99 ? "ok" : polB.uptime_percent >= 90 ? "warn" : "fail"}
                                        />
                                        <MetricTile
                                            label="Snapshots BET365"
                                            value={data?.total_snapshots_bet365_today ?? 0}
                                            hint="fixed odds del día"
                                            testid="audit-bet365-snapshots-count"
                                        />
                                        <MetricTile
                                            label="Gaps > 120s"
                                            value={polB.gaps_count ?? 0}
                                            hint={
                                                polB.longest_gap_seconds
                                                    ? `mayor: ${polB.longest_gap_seconds.toFixed(0)}s`
                                                    : "sin cortes"
                                            }
                                            testid="audit-bet365-gaps"
                                            tone={!polB.gaps_count ? "ok" : polB.gaps_count > 5 ? "fail" : "warn"}
                                        />
                                        <MetricTile
                                            label="Último heartbeat"
                                            value={
                                                polB.last_beat_age_seconds != null
                                                    ? `${Math.round(polB.last_beat_age_seconds)}s`
                                                    : "—"
                                            }
                                            hint={polB.last_beat_at ? fmtETShort(polB.last_beat_at) : "—"}
                                            testid="audit-bet365-last-beat"
                                            tone={
                                                polB.last_beat_age_seconds == null
                                                    ? "default"
                                                    : polB.last_beat_age_seconds > 180
                                                    ? "fail"
                                                    : polB.last_beat_age_seconds > 90
                                                    ? "warn"
                                                    : "ok"
                                            }
                                        />
                                    </div>
                                )}
                                {polB?.status_reason && (
                                    <div
                                        className="mt-3 text-[11px] font-mono text-warn/90"
                                        data-testid="audit-bet365-status-reason"
                                    >
                                        {polB.status_reason}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* -------- 4. SNAPSHOTS POR HORA (histograma) -------- */}
                        <Card>
                            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    Snapshots por hora — {data?.audit_date} ET
                                </CardTitle>
                                <div className="flex items-center gap-3 text-[11px] font-mono">
                                    <span className="flex items-center gap-1">
                                        <span className="inline-block w-3 h-3 bg-nyra rounded-sm" /> NYRA_RDL
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="inline-block w-3 h-3 bg-bet365 rounded-sm" /> BET365
                                    </span>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {hourlyChart.length > 0 ? (
                                    <div className="h-56 w-full" data-testid="audit-hourly-chart">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={hourlyChart} margin={{ top: 5, right: 15, bottom: 5, left: 0 }}>
                                                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                                                <XAxis
                                                    dataKey="hour"
                                                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "'Source Code Pro', monospace" }}
                                                    stroke="hsl(var(--border))"
                                                />
                                                <YAxis
                                                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "'Source Code Pro', monospace" }}
                                                    stroke="hsl(var(--border))"
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        background: "hsl(var(--card))",
                                                        border: "1px solid hsl(var(--border))",
                                                        borderRadius: 6,
                                                        color: "hsl(var(--card-foreground))",
                                                        fontFamily: "'Source Code Pro', monospace",
                                                        fontSize: 11,
                                                    }}
                                                    cursor={{ fill: "hsl(var(--muted) / 0.3)" }}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                                <Bar dataKey="NYRA" stackId="a" fill="hsl(var(--nyra))" isAnimationActive={false} />
                                                <Bar dataKey="BET365" stackId="a" fill="hsl(var(--bet365))" isAnimationActive={false} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="p-6 text-center text-xs text-muted-foreground" data-testid="audit-hourly-empty">
                                        Sin snapshots en {data?.audit_date} ET.
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {ca?.created_at && (
                            <div className="text-xs text-muted-foreground font-mono">
                                collection_audit actualizado: {fmtET(ca.created_at)}
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
